import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback, useState } from 'react';
import { useParams } from 'react-router-dom';

import { usePermissions } from '@/hooks/usePermissions';
import { Badge } from '@/lib/base/badges/badges';
import { Button } from '@/lib/base/buttons/button';
import { LoadingIndicator } from '@/lib/base/foundations/loading-indicator';
import { ConfirmationDialog } from '@/lib/base/overlays';
import { getApiBaseUrl } from '@/lib/config';
import { apiGet, apiPost } from '@/lib/http/client';

interface SlackStatus {
  connected: boolean;
  workspace_name?: string;
  workspace_id?: string;
  installed_by?: string;
  installed_at?: string;
  slack_client_id?: string;
}

interface DisconnectResponse {
  success: boolean;
  message: string;
}

export default function SlackIntegration() {
  const params = useParams();
  const { orgId } = params;
  const queryClient = useQueryClient();
  const { hasPermissionGroup } = usePermissions();
  const isAdmin = hasPermissionGroup('ahoy_other_admin');

  const [error, setError] = useState<string | null>(null);
  const [dismissedError, setDismissedError] = useState(false);
  const [isConfirmOpen, setIsConfirmOpen] = useState(false);

  // Fetch Slack status
  const {
    data: status,
    isLoading: loading,
    error: queryError,
  } = useQuery<SlackStatus>({
    queryKey: ['slackStatus', orgId],
    queryFn: async () => {
      return apiGet<SlackStatus>(
        `/api/orgs/${orgId}/integrations/slack/status/`
      );
    },
    enabled: !!orgId,
  });

  // Disconnect mutation
  const {
    mutateAsync: disconnectAsync,
    isPending: isDisconnecting,
    error: disconnectError,
  } = useMutation({
    mutationFn: async () => {
      return apiPost<DisconnectResponse>(
        `/api/orgs/${orgId}/integrations/slack/disconnect/`,
        {}
      );
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['slackStatus', orgId] });
    },
    onError: (err: Error) => {
      console.error(err);
      setError("We couldn't disconnect Slack. Try again.");
    },
  });

  const handleConnect = useCallback(() => {
    // Redirect to the Slack install endpoint which will initiate OAuth
    window.location.href = `${getApiBaseUrl()}/api/orgs/${orgId}/integrations/slack/install/`;
  }, [orgId]);

  const handleDisconnectClick = useCallback(() => setIsConfirmOpen(true), []);
  const handleCancelDisconnect = useCallback(() => setIsConfirmOpen(false), []);

  const handleConfirmDisconnect = useCallback(async () => {
    await disconnectAsync();
    setIsConfirmOpen(false);
  }, [disconnectAsync]);

  const clearError = () => {
    setDismissedError(true);
    setError(null);
  };

  // Derive displayed error
  const displayedError = dismissedError
    ? null
    : error || queryError?.message || disconnectError?.message || null;

  // Reset dismissedError when a new error comes in
  const currentErrorKey =
    error || queryError?.message || disconnectError?.message || '';
  const [lastErrorKey, setLastErrorKey] = useState(currentErrorKey);
  if (currentErrorKey !== lastErrorKey) {
    setLastErrorKey(currentErrorKey);
    if (currentErrorKey) {
      setDismissedError(false);
    }
  }

  if (loading) {
    return (
      <div className='flex justify-center py-8'>
        <LoadingIndicator size='sm' />
      </div>
    );
  }

  return (
    <div className='space-y-4'>
      {/* Error message */}
      {displayedError && (
        <div className='bg-utility-error-50 p-4 rounded-lg border border-utility-error-200'>
          <div className='flex items-start gap-3'>
            <svg
              className='w-5 h-5 text-utility-error-600 mt-0.5 shrink-0'
              fill='currentColor'
              viewBox='0 0 20 20'
            >
              <path
                fillRule='evenodd'
                d='M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z'
                clipRule='evenodd'
              />
            </svg>
            <div className='flex-1'>
              <p className='text-sm font-semibold text-utility-error-900 mb-1'>
                Unable to complete action
              </p>
              <p className='text-sm text-utility-error-700'>{displayedError}</p>
            </div>
            <button
              onClick={clearError}
              className='flex-shrink-0 text-utility-error-600 hover:text-utility-error-700'
            >
              <svg
                className='w-5 h-5'
                fill='none'
                stroke='currentColor'
                viewBox='0 0 24 24'
              >
                <path
                  strokeLinecap='round'
                  strokeLinejoin='round'
                  strokeWidth={2}
                  d='M6 18L18 6M6 6l12 12'
                />
              </svg>
            </button>
          </div>
        </div>
      )}

      {/* Slack Integration */}
      <div className='space-y-4'>
        <div className='flex items-center justify-between'>
          <div className='flex items-center gap-2'>
            <svg className='w-5 h-5' viewBox='0 0 24 24' fill='currentColor'>
              <path
                d='M5.042 15.165a2.528 2.528 0 0 1-2.52 2.523A2.528 2.528 0 0 1 0 15.165a2.527 2.527 0 0 1 2.522-2.52h2.52v2.52zM6.313 15.165a2.527 2.527 0 0 1 2.521-2.52 2.527 2.527 0 0 1 2.521 2.52v6.313A2.528 2.528 0 0 1 8.834 24a2.528 2.528 0 0 1-2.521-2.522v-6.313zM8.834 5.042a2.528 2.528 0 0 1-2.521-2.52A2.528 2.528 0 0 1 8.834 0a2.528 2.528 0 0 1 2.521 2.522v2.52H8.834zM8.834 6.313a2.528 2.528 0 0 1 2.521 2.521 2.528 2.528 0 0 1-2.521 2.521H2.522A2.528 2.528 0 0 1 0 8.834a2.528 2.528 0 0 1 2.522-2.521h6.312zM18.956 8.834a2.528 2.528 0 0 1 2.522-2.521A2.528 2.528 0 0 1 24 8.834a2.528 2.528 0 0 1-2.522 2.521h-2.522V8.834zM17.688 8.834a2.528 2.528 0 0 1-2.523 2.521 2.527 2.527 0 0 1-2.52-2.521V2.522A2.527 2.527 0 0 1 15.165 0a2.528 2.528 0 0 1 2.523 2.522v6.312zM15.165 18.956a2.528 2.528 0 0 1 2.523 2.522A2.528 2.528 0 0 1 15.165 24a2.527 2.527 0 0 1-2.52-2.522v-2.522h2.52zM15.165 17.688a2.527 2.527 0 0 1-2.52-2.523 2.526 2.526 0 0 1 2.52-2.52h6.313A2.527 2.527 0 0 1 24 15.165a2.528 2.528 0 0 1-2.522 2.523h-6.313z'
                fill='#E01E5A'
              />
            </svg>
            <h3 className='font-semibold text-primary'>Slack</h3>
          </div>
          {status?.connected ? (
            <Badge type='color' size='sm' color='success'>
              Connected
            </Badge>
          ) : (
            isAdmin && (
              <Button color='primary' size='sm' onClick={handleConnect}>
                Connect
              </Button>
            )
          )}
        </div>

        {status?.connected ? (
          <>
            {/* Connected Workspace Display */}
            <div className='bg-bg-secondary rounded-lg border border-border-secondary overflow-hidden'>
              <div className='flex items-center justify-between p-4'>
                <div className='flex items-center gap-3'>
                  <div className='w-8 h-8 bg-[#4A154B] rounded flex items-center justify-center'>
                    <svg className='w-5 h-5' viewBox='0 0 24 24' fill='white'>
                      <path d='M5.042 15.165a2.528 2.528 0 0 1-2.52 2.523A2.528 2.528 0 0 1 0 15.165a2.527 2.527 0 0 1 2.522-2.52h2.52v2.52zM6.313 15.165a2.527 2.527 0 0 1 2.521-2.52 2.527 2.527 0 0 1 2.521 2.52v6.313A2.528 2.528 0 0 1 8.834 24a2.528 2.528 0 0 1-2.521-2.522v-6.313zM8.834 5.042a2.528 2.528 0 0 1-2.521-2.52A2.528 2.528 0 0 1 8.834 0a2.528 2.528 0 0 1 2.521 2.522v2.52H8.834zM8.834 6.313a2.528 2.528 0 0 1 2.521 2.521 2.528 2.528 0 0 1-2.521 2.521H2.522A2.528 2.528 0 0 1 0 8.834a2.528 2.528 0 0 1 2.522-2.521h6.312zM18.956 8.834a2.528 2.528 0 0 1 2.522-2.521A2.528 2.528 0 0 1 24 8.834a2.528 2.528 0 0 1-2.522 2.521h-2.522V8.834zM17.688 8.834a2.528 2.528 0 0 1-2.523 2.521 2.527 2.527 0 0 1-2.52-2.521V2.522A2.527 2.527 0 0 1 15.165 0a2.528 2.528 0 0 1 2.523 2.522v6.312zM15.165 18.956a2.528 2.528 0 0 1 2.523 2.522A2.528 2.528 0 0 1 15.165 24a2.527 2.527 0 0 1-2.52-2.522v-2.522h2.52zM15.165 17.688a2.527 2.527 0 0 1-2.52-2.523 2.526 2.526 0 0 1 2.52-2.52h6.313A2.527 2.527 0 0 1 24 15.165a2.528 2.528 0 0 1-2.522 2.523h-6.313z' />
                    </svg>
                  </div>
                  <div>
                    <div className='flex items-center gap-2'>
                      <span className='font-medium text-primary'>
                        {status.workspace_name}
                      </span>
                    </div>
                    <p className='text-xs text-tertiary'>
                      {status.installed_by
                        ? `Connected by ${status.installed_by}`
                        : 'Connected workspace'}
                    </p>
                  </div>
                </div>
                {isAdmin && (
                  <div className='flex items-center gap-2'>
                    <Button color='secondary' size='sm' onClick={handleConnect}>
                      Reconnect
                    </Button>
                    <Button
                      color='secondary'
                      size='sm'
                      onClick={handleDisconnectClick}
                      disabled={isDisconnecting}
                    >
                      {isDisconnecting ? 'Disconnecting…' : 'Disconnect'}
                    </Button>
                  </div>
                )}
              </div>
            </div>

            {/* What this enables */}
            <div className='bg-bg-secondary p-4 rounded-lg border border-border-secondary'>
              <h4 className='font-semibold text-primary mb-2 text-sm'>
                Enabled features:
              </h4>
              <ul className='list-disc list-inside text-sm space-y-1 text-tertiary'>
                <li>
                  <strong className='text-primary'>
                    Meeting summary capture:
                  </strong>{' '}
                  Automatically capture and log meeting summaries from supported
                  meeting providers
                </li>
              </ul>
            </div>
          </>
        ) : (
          <>
            {/* What this enables */}
            <div className='bg-bg-secondary p-4 rounded-lg border border-border-secondary'>
              <h4 className='font-semibold text-primary mb-2 text-sm'>
                What you'll get:
              </h4>
              <ul className='list-disc list-inside text-sm space-y-1 text-tertiary'>
                <li>
                  <strong className='text-primary'>
                    Meeting summary capture:
                  </strong>{' '}
                  Automatically capture and log meeting summaries from supported
                  meeting providers
                </li>
              </ul>
            </div>
          </>
        )}
      </div>
      <ConfirmationDialog
        isOpen={isConfirmOpen}
        variant='warning'
        title='Disconnect Slack'
        description='Meeting summaries will no longer be captured from your workspace. You can reconnect any time.'
        confirmLabel='Disconnect'
        isLoading={isDisconnecting}
        onConfirm={handleConfirmDisconnect}
        onCancel={handleCancelDisconnect}
      />
    </div>
  );
}
